import * as React from 'react';

import NewsArchiveCtx from '@/context/news-archive-ctx';
import type { ArticleType } from '@/types/article-type';
import type { Category } from '@/utils/functions/wordpress/fetch-categories';
import queryAlgoliaPosts from '@/utils/functions/algolia/query-algolia-posts';
import mapAlgoliaHits from '@/utils/functions/algolia/map-algolia-hits';

import NewsFeed from '../components/news-feed/news-feed';
import SectionHeader from '../components/section-header';

type NewsArchiveProps = {
    articles: ArticleType[] | null;
    categories: Category[];
    authors: Category[];
    tags: Category[];
};

const NewsArchivePage = (props: NewsArchiveProps) => {
    const { articles, categories, authors, tags } = props;
    const [filteredPosts, setFilteredPosts] = React.useState<any>(articles);
    const prevPageRef = React.useRef<number>(0);

    const newsArchiveCtx = React.useContext(NewsArchiveCtx);

    React.useEffect(() => {
        const urlQuery = categories.filter((item: any) => item.slug === newsArchiveCtx.query);
        if (urlQuery.length > 0) {
            newsArchiveCtx.setCategories([...newsArchiveCtx.categories, ...urlQuery]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newsArchiveCtx.query]);

    // Fetch post by filters
    const getFilteredPosts = async () => {
        newsArchiveCtx.setShowLoader(true);

        const limit = 16;

        const authorFilters: string[] = [];
        if (newsArchiveCtx.author) {
            authorFilters.push(`taxonomies.author:${newsArchiveCtx.author.name}`);
        }
        const categoryFilters: string[] = [];
        newsArchiveCtx.categories.forEach((category: any) => {
            categoryFilters.push(`taxonomies.category:${category.id}`);
        });
        const tagFilters: string[] = [];
        newsArchiveCtx.tags.forEach((tag: any) => {
            tagFilters.push(`taxonomies.post_tag:${tag.id}`);
        });

        const algoliaResult = await queryAlgoliaPosts('', limit, newsArchiveCtx.page, {
            facetFilters: [...authorFilters, ...categoryFilters, ...tagFilters],
        });
        const result = mapAlgoliaHits(algoliaResult.hits);

        // Get filtered posts first page
        newsArchiveCtx.setShowLoader(false);

        if (result.length < limit) {
            newsArchiveCtx.setHasData(false);
        } else {
            newsArchiveCtx.setHasData(true);
        }

        setFilteredPosts(newsArchiveCtx.page > prevPageRef.current ? [...filteredPosts, ...result] : result);

        prevPageRef.current = newsArchiveCtx.page;
    };

    // Fetch new posts after new filter value is selected
    React.useEffect(() => {
        // Only fetch filtered posts if a filter value is selected
        if (
            newsArchiveCtx.author ||
            newsArchiveCtx.categories.length > 0 ||
            newsArchiveCtx.tags.length > 0 ||
            newsArchiveCtx.page > 0
        ) {
            getFilteredPosts();
        } else {
            setFilteredPosts(articles);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        newsArchiveCtx.page,
        newsArchiveCtx.author,
        newsArchiveCtx.categories,
        newsArchiveCtx.tags,
        articles,
        setFilteredPosts,
    ]);

    return (
        <main className="w-full">
            <section className="flex flex-row">
                <SectionHeader
                    categories={categories}
                    tags={tags}
                    authors={authors}
                    showLoader={newsArchiveCtx.showLoader}
                />
            </section>
            <section>
                <NewsFeed articles={filteredPosts || articles} />
            </section>
        </main>
    );
};

export default NewsArchivePage;
